import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import {
  adminGetEmailOverview,
  adminListEmailCampaigns,
  adminDeleteEmailCampaign,
  adminSendEmailCampaign,
  adminCancelEmailCampaign,
  adminGetCampaignStats,
  adminGetCampaignRecipients,
} from "./adminClient";
import EmailCampaignFormModal from "./EmailCampaignFormModal";

/* ─── Animations ─────────────────────────────────────────────────────── */
const fadeUp  = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;

/* ─── Styled (cohérent avec AdminDashboard) ──────────────────────────── */
const StatGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 1rem; margin-bottom: 2rem;
`;
const StatCard = styled.div`
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.border};
  border-radius:12px; padding:1rem 1.25rem;
  animation:${fadeUp} .4s ease both;
  animation-delay:${({$i})=>$i*.06}s;
  border-left:3px solid ${({$c,theme})=>theme[$c]||theme.border};
`;
const StatVal = styled.div`
  font-family:${({theme})=>theme.fontMono};
  font-size:1.3rem; font-weight:500; line-height:1;
  color:${({$c,theme})=>theme[$c]||theme.text};
`;
const StatLbl = styled.div`
  font-size:.62rem; text-transform:uppercase; letter-spacing:.08em;
  color:${({theme})=>theme.textMuted}; margin-top:.4rem;
`;
const StatSub = styled.div`
  font-family:${({theme})=>theme.fontMono};
  font-size:.62rem; color:${({theme})=>theme.textMuted}; margin-top:.2rem;
`;
const QuotaBar = styled.div`
  height:3px; background:${({theme})=>theme.bg};
  border-radius:100px; margin-top:.5rem; overflow:hidden;
`;
const QuotaFill = styled.div`
  height:100%; width:${({$pct})=>$pct}%;
  background:linear-gradient(90deg, ${({theme,$pct})=>$pct>80?theme.red:$pct>60?theme.gold:theme.green}, ${({theme,$pct})=>$pct>80?theme.red:$pct>60?theme.gold:theme.green}88);
  transition:width .3s;
`;

const Panel = styled.div`
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.border};
  border-radius:12px; overflow:hidden; margin-bottom:1.5rem;
`;
const PanelHead = styled.div`
  display:flex; align-items:center; justify-content:space-between;
  flex-wrap:wrap; gap:.5rem; padding:.85rem 1.25rem;
  background:${({theme})=>theme.bg};
  border-bottom:1px solid ${({theme})=>theme.border};
`;
const PanelTitle = styled.span`
  font-family:${({theme})=>theme.fontMono};
  font-size:.67rem; text-transform:uppercase; letter-spacing:.08em;
  color:${({theme})=>theme.textMuted};
`;
const PanelActions = styled.div`display:flex; gap:.5rem; align-items:center; flex-wrap:wrap;`;
const FilterChip = styled.button`
  font-family:${({theme})=>theme.fontMono};
  font-size:.62rem; padding:.22rem .6rem;
  border-radius:100px; letter-spacing:.05em; cursor:pointer;
  border:1px solid ${({$active,theme})=>$active?theme.borderLight:theme.border};
  background:${({$active,theme})=>$active?theme.bgHover:"transparent"};
  color:${({$active,theme})=>$active?theme.text:theme.textMuted};
  &:hover { border-color:${({theme})=>theme.borderLight}; }
`;
const Search = styled.input`
  background:${({theme})=>theme.bg};
  border:1px solid ${({theme})=>theme.border};
  border-radius:7px;
  color:${({theme})=>theme.text};
  font-family:${({theme})=>theme.fontBody};
  font-size:.75rem; padding:.35rem .75rem; outline:none; width:160px;
  &:focus { border-color:${({theme})=>theme.borderLight}; }
  &::placeholder { color:${({theme})=>theme.textMuted}; }
`;
const NewBtn = styled.button`
  display:flex; align-items:center; gap:.35rem;
  padding:.4rem .9rem; border-radius:7px;
  border:1px solid ${({theme})=>theme.accent}66;
  background:rgba(79,195,247,.08);
  color:${({theme})=>theme.accent};
  font-family:${({theme})=>theme.fontBody};
  font-size:.75rem; font-weight:600; cursor:pointer;
  &:hover { opacity:.8; }
`;

const TableScroll = styled.div`overflow-x:auto; -webkit-overflow-scrolling:touch;`;
const THead = styled.div`
  display:grid;
  grid-template-columns:${({$cols})=>$cols};
  gap:.5rem; padding:.6rem 1.25rem;
  background:${({theme})=>theme.bg};
  border-bottom:1px solid ${({theme})=>theme.border};
  min-width:${({$minW})=>$minW||"auto"};
`;
const TH = styled.span`
  font-family:${({theme})=>theme.fontMono};
  font-size:.6rem; text-transform:uppercase; letter-spacing:.08em;
  color:${({theme})=>theme.textMuted};
`;
const TRow = styled.div`
  display:grid;
  grid-template-columns:${({$cols})=>$cols};
  gap:.5rem; padding:.85rem 1.25rem;
  border-bottom:1px solid ${({theme})=>theme.border};
  align-items:center; transition:background .15s;
  min-width:${({$minW})=>$minW||"auto"};
  &:last-child { border-bottom:none; }
  &:hover { background:${({theme})=>theme.bgHover}; }
`;
const Cell = styled.div`
  font-size:.78rem;
  color:${({$dim,theme})=>$dim?theme.textDim:theme.text};
  font-family:${({$mono,theme})=>$mono?theme.fontMono:"inherit"};
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
`;
const Pill = styled.span`
  font-family:${({theme})=>theme.fontMono};
  font-size:.6rem; padding:.18rem .55rem;
  border-radius:100px; letter-spacing:.05em;
  background:${({$s,theme}) =>
    $s==="SENT"     ? theme.greenDim :
    $s==="SENDING"  ? "rgba(79,195,247,.1)" :
    $s==="QUEUED"   ? "rgba(255,193,7,.1)" :
    $s==="DRAFT"    ? theme.bgHover :
    $s==="FAILED"||$s==="CANCELLED" ? theme.redDim :
                      theme.bgHover};
  color:${({$s,theme}) =>
    $s==="SENT"     ? theme.green :
    $s==="SENDING"  ? theme.accent :
    $s==="QUEUED"   ? theme.gold :
    $s==="DRAFT"    ? theme.textMuted :
    $s==="FAILED"||$s==="CANCELLED" ? theme.red :
                      theme.textMuted};
`;
const ActionBtns = styled.div`display:flex; gap:.35rem; flex-wrap:wrap;`;
const Btn = styled.button`
  font-family:${({theme})=>theme.fontMono};
  font-size:.63rem; padding:.25rem .65rem;
  border-radius:5px; cursor:pointer; letter-spacing:.04em;
  transition:all .15s; white-space:nowrap;
  border:1px solid ${({$t,theme}) =>
    $t==="ok"?theme.green+"55":
    $t==="warn"?theme.gold+"55":
    $t==="danger"?theme.red+"55":theme.borderLight};
  background:${({$t,theme}) =>
    $t==="ok"?theme.greenDim:
    $t==="warn"?"rgba(255,193,7,.08)":
    $t==="danger"?theme.redDim:theme.bgHover};
  color:${({$t,theme}) =>
    $t==="ok"?theme.green:
    $t==="warn"?theme.gold:
    $t==="danger"?theme.red:theme.textDim};
  &:disabled { opacity:.35; cursor:not-allowed; }
  &:hover:not(:disabled) { opacity:.8; }
`;
const Empty = styled.div`
  text-align:center; padding:2.5rem;
  font-size:.8rem; color:${({theme})=>theme.textMuted};
`;
const Skel = styled.div`
  height:48px; margin:.3rem 1.25rem; border-radius:6px;
  background:linear-gradient(90deg, ${({theme})=>theme.bgCard} 25%, ${({theme})=>theme.bgHover} 50%, ${({theme})=>theme.bgCard} 75%);
  background-size:400px 100%; animation:${shimmer} 1.4s infinite;
`;

/* ─── Helpers ────────────────────────────────────────────────────────── */
const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day:"numeric", month:"short", year:"numeric",
    hour:"2-digit", minute:"2-digit",
  });
};
const fmtNum = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n/1_000).toFixed(1)}K`;
  return String(n);
};
const extractErr = (err, fallback="Erreur serveur.") => {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === "string") return data;
  if (data.detail) return data.detail;
  return JSON.stringify(data).slice(0, 200);
};

/* ════════════════════════════════════════════════════════════════════════
   MAIN TAB
════════════════════════════════════════════════════════════════════════ */
export default function EmailCampaignsTab({ toast }) {
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);   // { type: "create" | "edit", campaign }
  const [acting, setActing] = useState(null);

  /* ── Load overview + campaigns ─────────────────────────────────────── */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, campRes] = await Promise.all([
        adminGetEmailOverview(),
        adminListEmailCampaigns({
          status: filter !== "ALL" ? filter : undefined,
          search: search || undefined,
        }),
      ]);
      setOverview(ovRes.data);
      setCampaigns(campRes.data.results || campRes.data || []);
    } catch (err) {
      toast.err(extractErr(err, "Impossible de charger les campagnes."));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(loadAll, 250);   // debounce search
    return () => clearTimeout(t);
  }, [loadAll]);

  /* ── Polling automatique pour les campagnes SENDING ───────────────── */
  useEffect(() => {
    const hasSending = campaigns.some(c => c.status === "SENDING" || c.status === "QUEUED");
    if (!hasSending) return;

    const interval = setInterval(() => {
      loadAll();
    }, 3000);
    return () => clearInterval(interval);
  }, [campaigns, loadAll]);

  /* ── Actions ───────────────────────────────────────────────────────── */
  const handleSend = async (campaign) => {
    if (!window.confirm(
      `Envoyer "${campaign.name}" à ${campaign.estimated_recipients || "?"} destinataires ?\n\n` +
      `Cette action est irréversible une fois lancée.`
    )) return;

    setActing(campaign.id);
    try {
      const r = await adminSendEmailCampaign(campaign.id);
      toast.ok(`Campagne en cours d'envoi : ${r.data.estimated_recipients} destinataires.`);
      loadAll();
    } catch (err) {
      toast.err(extractErr(err, "Impossible de lancer l'envoi."));
    } finally {
      setActing(null);
    }
  };

  const handleCancel = async (campaign) => {
    if (!window.confirm(
      `Annuler "${campaign.name}" ?\n\n` +
      (campaign.status === "SENDING"
        ? `${campaign.sent_count} emails déjà envoyés ne pourront pas être rappelés.`
        : `Les destinataires restants ne recevront pas l'email.`)
    )) return;

    setActing(campaign.id);
    try {
      await adminCancelEmailCampaign(campaign.id);
      toast.ok("Campagne annulée.");
      loadAll();
    } catch (err) {
      toast.err(extractErr(err, "Impossible d'annuler."));
    } finally {
      setActing(null);
    }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Supprimer définitivement "${campaign.name}" ?`)) return;

    setActing(campaign.id);
    try {
      await adminDeleteEmailCampaign(campaign.id);
      toast.ok("Campagne supprimée.");
      loadAll();
    } catch (err) {
      toast.err(extractErr(err, "Impossible de supprimer."));
    } finally {
      setActing(null);
    }
  };

  /* ── Render ────────────────────────────────────────────────────────── */
  const ov = overview || {};
  const brevoUsed = ov.brevo_used_today || 0;
  const brevoLimit = ov.brevo_daily_limit || 300;
  const quotaPct = Math.min(100, (brevoUsed / brevoLimit) * 100);

  const COLS = "2fr 100px 90px 70px 70px 70px 140px 160px";
  const MIN  = "950px";

  return (
    <>
      {/* ════ OVERVIEW STATS ════ */}
      <StatGrid>
        <StatCard $c="green" $i={0}>
          <StatLbl>Envoyés aujourd'hui</StatLbl>
          <StatVal $c="green">{fmtNum(brevoUsed)} / {brevoLimit}</StatVal>
          <QuotaBar>
            <QuotaFill $pct={quotaPct}/>
          </QuotaBar>
          <StatSub>{Math.max(0, brevoLimit - brevoUsed)} restants (quota Brevo)</StatSub>
        </StatCard>

        <StatCard $c="accent" $i={1}>
          <StatLbl>Taux d'ouverture (30j)</StatLbl>
          <StatVal $c="accent">{ov.avg_open_rate ?? 0}%</StatVal>
          <StatSub>{fmtNum(ov.sent_month || 0)} emails envoyés</StatSub>
        </StatCard>

        <StatCard $c="gold" $i={2}>
          <StatLbl>Taux de clic (30j)</StatLbl>
          <StatVal $c="gold">{ov.avg_click_rate ?? 0}%</StatVal>
          <StatSub>Médiane secteur : 2-5%</StatSub>
        </StatCard>

        <StatCard $c={ov.avg_bounce_rate > 5 ? "red" : "text"} $i={3}>
          <StatLbl>Taux de bounce (30j)</StatLbl>
          <StatVal $c={ov.avg_bounce_rate > 5 ? "red" : "text"}>{ov.avg_bounce_rate ?? 0}%</StatVal>
          <StatSub>{ov.avg_bounce_rate > 5 ? "⚠ Hygiène à vérifier" : "✓ Sain"}</StatSub>
        </StatCard>

        <StatCard $c={ov.active_campaigns > 0 ? "accent" : "text"} $i={4}>
          <StatLbl>Campagnes actives</StatLbl>
          <StatVal $c={ov.active_campaigns > 0 ? "accent" : "text"}>{ov.active_campaigns ?? 0}</StatVal>
          <StatSub>En envoi ou planifiées</StatSub>
        </StatCard>

        <StatCard $c={ov.unsubs_week > 5 ? "gold" : "text"} $i={5}>
          <StatLbl>Désabos (7j)</StatLbl>
          <StatVal $c={ov.unsubs_week > 5 ? "gold" : "text"}>{ov.unsubs_week ?? 0}</StatVal>
          <StatSub>{ov.unsubs_total ?? 0} total</StatSub>
        </StatCard>
      </StatGrid>

      {/* ════ CAMPAIGNS LIST ════ */}
      <Panel>
        <PanelHead>
          <PanelTitle>Campagnes ({campaigns.length})</PanelTitle>
          <PanelActions>
            <Search
              placeholder="Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {["ALL","DRAFT","QUEUED","SENDING","SENT","FAILED","CANCELLED"].map(f => (
              <FilterChip key={f} $active={filter===f} onClick={() => setFilter(f)}>{f}</FilterChip>
            ))}
            <NewBtn onClick={() => setModal({type:"create"})}>
              + Nouvelle campagne
            </NewBtn>
          </PanelActions>
        </PanelHead>

        <TableScroll>
          <THead $cols={COLS} $minW={MIN}>
            <TH>Nom</TH>
            <TH>Statut</TH>
            <TH>Cibles</TH>
            <TH>Envoyés</TH>
            <TH>Ouverts</TH>
            <TH>CTR</TH>
            <TH>Créée</TH>
            <TH>Actions</TH>
          </THead>

          {loading
            ? Array.from({length:4}).map((_,i) => <Skel key={i}/>)
            : campaigns.length === 0
            ? <Empty>
                Aucune campagne {filter !== "ALL" && `en statut ${filter}`}.
                <br/>
                <span style={{fontSize:".75rem"}}>
                  Clique sur "+ Nouvelle campagne" pour commencer.
                </span>
              </Empty>
            : campaigns.map(c => {
              const isFinal = ["SENT","FAILED","CANCELLED"].includes(c.status);
              const isActing = acting === c.id;
              return (
                <TRow key={c.id} $cols={COLS} $minW={MIN}>
                  <Cell style={{whiteSpace:"normal",lineHeight:1.3}}>
                    <div style={{fontWeight:500,fontSize:".78rem"}}>{c.name}</div>
                    {c.scheduled_at && c.status === "QUEUED" && (
                      <div style={{fontSize:".62rem",color:"#ffc107",fontFamily:"DM Mono,monospace"}}>
                        ⏰ Planifiée pour {fmt(c.scheduled_at)}
                      </div>
                    )}
                  </Cell>
                  <Cell><Pill $s={c.status}>{c.status}</Pill></Cell>
                  <Cell $mono>{fmtNum(c.estimated_recipients)}</Cell>
                  <Cell $mono>
                    {fmtNum(c.sent_count)}
                    {c.status === "SENDING" && c.estimated_recipients > 0 && (
                      <span style={{color:"#5a6a7a",marginLeft:".25rem",fontSize:".68rem"}}>
                        /{c.estimated_recipients}
                      </span>
                    )}
                  </Cell>
                  <Cell $mono style={{color: c.open_rate > 20 ? "#00e676" : "#e8edf2"}}>
                    {c.opened_count > 0
                      ? `${c.opened_count} (${c.open_rate}%)`
                      : "—"}
                  </Cell>
                  <Cell $mono style={{color: c.click_rate > 5 ? "#4fc3f7" : "#e8edf2"}}>
                    {c.click_rate > 0 ? `${c.click_rate}%` : "—"}
                  </Cell>
                  <Cell $dim style={{fontSize:".7rem"}}>{fmt(c.created_at)}</Cell>
                  <Cell>
                    <ActionBtns>
                      {c.status === "DRAFT" && (
                        <>
                          <Btn onClick={() => setModal({type:"edit", campaign:c})}>✎ Éditer</Btn>
                          <Btn $t="ok" disabled={isActing} onClick={() => handleSend(c)}>
                            ▶ Envoyer
                          </Btn>
                          <Btn $t="danger" disabled={isActing} onClick={() => handleDelete(c)}>
                            🗑
                          </Btn>
                        </>
                      )}
                      {c.status === "QUEUED" && (
                        <>
                          <Btn onClick={() => setModal({type:"edit", campaign:c})}>✎ Éditer</Btn>
                          <Btn $t="warn" disabled={isActing} onClick={() => handleCancel(c)}>
                            ✕ Annuler
                          </Btn>
                        </>
                      )}
                      {c.status === "SENDING" && (
                        <>
                          <Btn $t="warn" disabled={isActing} onClick={() => handleCancel(c)}>
                            ✕ Stopper
                          </Btn>
                          <span style={{
                            fontSize:".62rem",
                            color:"#4fc3f7",
                            fontFamily:"DM Mono,monospace",
                          }}>
                            ⟳ {Math.round((c.sent_count / Math.max(c.estimated_recipients, 1)) * 100)}%
                          </span>
                        </>
                      )}
                      {isFinal && (
                        <Btn onClick={() => setModal({type:"view", campaign:c})}>
                          📊 Stats
                        </Btn>
                      )}
                    </ActionBtns>
                  </Cell>
                </TRow>
              );
            })
          }
        </TableScroll>
      </Panel>

      {/* ════ MODALS ════ */}
      {modal?.type === "create" && (
        <EmailCampaignFormModal
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadAll(); }}
          toast={toast}
        />
      )}
      {modal?.type === "edit" && (
        <EmailCampaignFormModal
          campaign={modal.campaign}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadAll(); }}
          toast={toast}
        />
      )}
      {modal?.type === "view" && (
        <CampaignStatsModal
          campaign={modal.campaign}
          onClose={() => setModal(null)}
          toast={toast}
        />
      )}
    </>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   STATS MODAL — pour les campagnes SENT/FAILED/CANCELLED
════════════════════════════════════════════════════════════════════════ */
const Overlay = styled.div`
  position:fixed; inset:0; z-index:500;
  background:rgba(0,0,0,.82); backdrop-filter:blur(8px);
  display:flex; align-items:center; justify-content:center; padding:1rem;
`;
const ModalBox = styled.div`
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.borderLight};
  border-radius:16px; width:100%; max-width:${({$w})=>$w||"720px"};
  max-height:90vh; overflow-y:auto;
`;
const MHead = styled.div`
  padding:1.1rem 1.4rem;
  border-bottom:1px solid ${({theme})=>theme.border};
  display:flex; align-items:center; justify-content:space-between;
  position:sticky; top:0;
  background:${({theme})=>theme.bgCard}; z-index:1;
`;
const MTitle = styled.h3`
  font-family:${({theme})=>theme.fontDisplay};
  font-size:1rem; font-weight:700; letter-spacing:-.02em;
`;
const MClose = styled.button`
  background:none; border:none;
  color:${({theme})=>theme.textMuted};
  font-size:1rem; cursor:pointer;
  &:hover { color:${({theme})=>theme.text}; }
`;
const MBody = styled.div`
  padding:1.25rem 1.4rem;
  display:flex; flex-direction:column; gap:1rem;
`;

function CampaignStatsModal({ campaign, onClose, toast }) {
  const [stats, setStats] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [tab, setTab] = useState("stats");
  const [recStatus, setRecStatus] = useState("ALL");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [sRes, rRes] = await Promise.all([
          adminGetCampaignStats(campaign.id),
          adminGetCampaignRecipients(campaign.id, { page_size: 50 }),
        ]);
        setStats(sRes.data);
        setRecipients(rRes.data.results || []);
      } catch (err) {
        toast.err(extractErr(err, "Impossible de charger les stats."));
      } finally {
        setLoading(false);
      }
    })();
  }, [campaign.id]);

  const reloadRecipients = async (status) => {
    try {
      const r = await adminGetCampaignRecipients(campaign.id, {
        status: status === "ALL" ? undefined : status,
        page_size: 50,
      });
      setRecipients(r.data.results || []);
    } catch {}
  };

  useEffect(() => {
    if (tab === "recipients") reloadRecipients(recStatus);
  }, [recStatus, tab]);

  return (
    <Overlay onClick={e => e.target === e.currentTarget && onClose()}>
      <ModalBox $w="800px">
        <MHead>
          <MTitle>
            📊 {campaign.name}
            <span style={{marginLeft:".75rem"}}>
              <Pill $s={campaign.status}>{campaign.status}</Pill>
            </span>
          </MTitle>
          <MClose onClick={onClose}>✕</MClose>
        </MHead>

        <MBody>
          {loading
            ? <div style={{textAlign:"center",padding:"2rem",color:"#5a6a7a"}}>Chargement…</div>
            : !stats
            ? <Empty>Aucune donnée.</Empty>
            : <>
              {/* Toggle */}
              <div style={{display:"flex",gap:".4rem"}}>
                <FilterChip $active={tab==="stats"} onClick={() => setTab("stats")}>
                  Statistiques
                </FilterChip>
                <FilterChip $active={tab==="recipients"} onClick={() => setTab("recipients")}>
                  Destinataires ({stats.sent_count + stats.failed_count})
                </FilterChip>
              </div>

              {/* STATS TAB */}
              {tab === "stats" && (
                <>
                  <StatGrid style={{marginBottom:".5rem"}}>
                    <StatCard $c="text" $i={0}>
                      <StatLbl>Cibles</StatLbl>
                      <StatVal>{fmtNum(stats.estimated_recipients)}</StatVal>
                    </StatCard>
                    <StatCard $c="green" $i={1}>
                      <StatLbl>Envoyés</StatLbl>
                      <StatVal $c="green">{fmtNum(stats.sent_count)}</StatVal>
                      <StatSub>{stats.failed_count > 0 && `${stats.failed_count} échecs`}</StatSub>
                    </StatCard>
                    <StatCard $c="accent" $i={2}>
                      <StatLbl>Ouverts</StatLbl>
                      <StatVal $c="accent">{stats.open_rate}%</StatVal>
                      <StatSub>{fmtNum(stats.opened_count)} / {fmtNum(stats.sent_count)}</StatSub>
                    </StatCard>
                    <StatCard $c="gold" $i={3}>
                      <StatLbl>Cliqués</StatLbl>
                      <StatVal $c="gold">{stats.click_rate}%</StatVal>
                      <StatSub>{fmtNum(stats.clicked_count)} clics</StatSub>
                    </StatCard>
                    <StatCard $c={stats.bounce_rate > 5 ? "red" : "text"} $i={4}>
                      <StatLbl>Bounce</StatLbl>
                      <StatVal $c={stats.bounce_rate > 5 ? "red" : "text"}>
                        {stats.bounce_rate}%
                      </StatVal>
                      <StatSub>{fmtNum(stats.bounced_count)}</StatSub>
                    </StatCard>
                    <StatCard $c={stats.unsubscribed_count > 0 ? "gold" : "text"} $i={5}>
                      <StatLbl>Désabos</StatLbl>
                      <StatVal $c={stats.unsubscribed_count > 0 ? "gold" : "text"}>
                        {fmtNum(stats.unsubscribed_count)}
                      </StatVal>
                    </StatCard>
                  </StatGrid>

                  {/* Breakdown par langue */}
                  {Object.keys(stats.by_language || {}).length > 0 && (
                    <div style={{
                      background:"#080a0c",
                      border:"1px solid #1e2530",
                      borderRadius:"8px",
                      padding:".85rem 1rem",
                    }}>
                      <div style={{
                        fontFamily:"DM Mono,monospace",
                        fontSize:".62rem",
                        color:"#5a6a7a",
                        textTransform:"uppercase",
                        letterSpacing:".08em",
                        marginBottom:".5rem",
                      }}>
                        Par langue
                      </div>
                      <div style={{display:"flex",gap:".75rem",flexWrap:"wrap"}}>
                        {Object.entries(stats.by_language).map(([lang, count]) => (
                          <span key={lang} style={{
                            fontFamily:"DM Mono,monospace",
                            fontSize:".72rem",
                            color:"#e8edf2",
                          }}>
                            {lang.toUpperCase()} :{" "}
                            <strong style={{color:"#4fc3f7"}}>{count}</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Breakdown par statut */}
                  {Object.keys(stats.by_status || {}).length > 0 && (
                    <div style={{
                      background:"#080a0c",
                      border:"1px solid #1e2530",
                      borderRadius:"8px",
                      padding:".85rem 1rem",
                    }}>
                      <div style={{
                        fontFamily:"DM Mono,monospace",
                        fontSize:".62rem",
                        color:"#5a6a7a",
                        textTransform:"uppercase",
                        letterSpacing:".08em",
                        marginBottom:".5rem",
                      }}>
                        Par statut
                      </div>
                      <div style={{display:"flex",gap:".75rem",flexWrap:"wrap"}}>
                        {Object.entries(stats.by_status).map(([s, count]) => (
                          <span key={s} style={{
                            fontFamily:"DM Mono,monospace",
                            fontSize:".72rem",
                          }}>
                            <Pill $s={s}>{s}</Pill>{" "}
                            <strong style={{color:"#e8edf2"}}>{count}</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* RECIPIENTS TAB */}
              {tab === "recipients" && (
                <>
                  <div style={{display:"flex",gap:".4rem",flexWrap:"wrap"}}>
                    {["ALL","SENT","OPENED","CLICKED","FAILED","BOUNCED","UNSUBSCRIBED"].map(s => (
                      <FilterChip key={s} $active={recStatus===s} onClick={() => setRecStatus(s)}>
                        {s}
                      </FilterChip>
                    ))}
                  </div>

                  <div style={{
                    border:"1px solid #1e2530",
                    borderRadius:"8px",
                    overflowX:"auto",
                  }}>
                    <table style={{
                      width:"100%",
                      borderCollapse:"collapse",
                      fontSize:".7rem",
                      minWidth:"600px",
                    }}>
                      <thead>
                        <tr style={{background:"#080a0c"}}>
                          {["Username","Email","Lang","Status","Envoyé","Ouvert"].map(h => (
                            <th key={h} style={{
                              padding:".5rem .65rem",
                              textAlign:"left",
                              fontFamily:"DM Mono,monospace",
                              fontSize:".6rem",
                              color:"#5a6a7a",
                              textTransform:"uppercase",
                              borderBottom:"1px solid #1e2530",
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {recipients.length === 0
                          ? <tr><td colSpan="6" style={{
                              padding:"2rem",
                              textAlign:"center",
                              color:"#5a6a7a",
                            }}>Aucun destinataire.</td></tr>
                          : recipients.map(r => (
                            <tr key={r.id} style={{borderBottom:"1px solid #1e2530"}}>
                              <td style={{
                                padding:".5rem .65rem",
                                fontFamily:"DM Mono,monospace",
                                fontSize:".7rem",
                              }}>{r.username}</td>
                              <td style={{
                                padding:".5rem .65rem",
                                fontSize:".68rem",
                                color:"#8a9aaa",
                              }}>{r.email}</td>
                              <td style={{
                                padding:".5rem .65rem",
                                fontFamily:"DM Mono,monospace",
                                fontSize:".68rem",
                                color:"#4fc3f7",
                              }}>{r.language?.toUpperCase()}</td>
                              <td style={{padding:".5rem .65rem"}}>
                                <Pill $s={r.status}>{r.status}</Pill>
                              </td>
                              <td style={{
                                padding:".5rem .65rem",
                                fontSize:".65rem",
                                color:"#8a9aaa",
                              }}>{fmt(r.sent_at)}</td>
                              <td style={{
                                padding:".5rem .65rem",
                                fontSize:".65rem",
                                color: r.opened_at ? "#00e676" : "#5a6a7a",
                              }}>{r.opened_at ? "✓ " + fmt(r.opened_at) : "—"}</td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          }
        </MBody>
      </ModalBox>
    </Overlay>
  );
}